/**
 * Ported from sara/app `src/components/ear-reader/use-quiz-cooldown.ts` @
 * e8e8fecb4ec5.
 *
 * Bindery is canonical for this module from 2026-09-04; reconcile app-side
 * drift toward this copy. Deliberate divergences from the source:
 *
 * - The source's `mountedRef` guard is gone. Under `<StrictMode>`'s
 *   mount → cleanup → remount, the cleanup set it false and nothing set it
 *   back, so every scheduled transition was dropped and the first wrong
 *   answer locked the drawer permanently in dev. The unmount cleanup already
 *   clears every pending timer, so no callback can outlive the component —
 *   the guard bought nothing and broke StrictMode.
 * - `triggerWrong` now cancels any pending cooldown chain before starting a
 *   new one, so a second wrong during a cooldown cannot leave the first
 *   chain's `fullReset` firing mid-flight (and `timersRef` no longer grows
 *   unboundedly across a long quiz).
 * - The timing is a parameter (`CooldownTiming`), and the schedule a wrong
 *   answer runs is the pure `cooldownSchedule`. This is the ONE wrong-answer
 *   cooldown in the workspace: the engagement gate (retells' gloss popover,
 *   lekterion's selection menu) used to carry its own 3-phase copy, and now
 *   runs this machine with `GATE_COOLDOWN`. The drawer's own timing is
 *   `QUIZ_DRAWER_COOLDOWN`, the default, unchanged from the source.
 */

import { useState, useEffect, useRef, useCallback } from "react";

export type CooldownPhase = "idle" | "shaking" | "faded" | "staggerReturn";

/** One wrong answer's timeline, in ms from the pick. */
export interface CooldownStep {
  /** Shaking ends; the options fade. */
  fadeAt: number;
  /** The options return (`staggerReturn`), reshuffled if `reshuffle`. */
  returnAt: number;
  /** Back to idle, unlocked. `returnAt === resetAt` skips `staggerReturn`. */
  resetAt: number;
  reshuffle: boolean;
}

export interface CooldownTiming {
  /** The first wrong answers, before the cooldown turns hard. */
  soft: CooldownStep;
  /** From the `hardAfter`-th wrong answer on. */
  hard: CooldownStep;
  hardAfter: number;
}

/**
 * The quiz drawer's timing (sara/app prototype): a soft miss fades to 0.3
 * and returns the same options; a hard one fades out, reshuffles and
 * staggers back in.
 */
export const QUIZ_DRAWER_COOLDOWN: CooldownTiming = {
  soft: { fadeAt: 500, returnAt: 1400, resetAt: 1700, reshuffle: false },
  hard: { fadeAt: 500, returnAt: 2000, resetAt: 2800, reshuffle: true },
  hardAfter: 2,
};

/**
 * The engagement gate's timing: shake, a 260 ms fade, and a fresh shuffled
 * set straight back — every miss, soft or hard (only the shake differs). Kept
 * at the gate's shipped 500 + 260 ms rather than moved to the drawer's: the
 * gate stands between a reader and a translation mid-sentence, where a
 * 1.7–2.8 s lockout is a penalty for reading, and both apps shipped this.
 */
export const GATE_COOLDOWN: CooldownTiming = {
  soft: { fadeAt: 500, returnAt: 760, resetAt: 760, reshuffle: true },
  hard: { fadeAt: 500, returnAt: 760, resetAt: 760, reshuffle: true },
  hardAfter: 2,
};

/** A timed transition a wrong answer schedules. */
export interface CooldownTransition {
  at: number;
  phase: Exclude<CooldownPhase, "shaking">;
  /** Deal a fresh set of options at this transition. */
  reshuffle: boolean;
}

/**
 * The transitions the `wrongCount`-th wrong answer runs, in order. Pure: the
 * hook only schedules them. Whether the miss is hard is `wrongCount >=
 * hardAfter` (so `wrongCount` counts this miss).
 */
export function cooldownSchedule(
  timing: CooldownTiming,
  wrongCount: number,
): CooldownTransition[] {
  const step = wrongCount >= timing.hardAfter ? timing.hard : timing.soft;
  const faded: CooldownTransition = {
    at: step.fadeAt,
    phase: "faded",
    reshuffle: false,
  };
  if (step.returnAt >= step.resetAt) {
    return [
      faded,
      { at: step.resetAt, phase: "idle", reshuffle: step.reshuffle },
    ];
  }
  return [
    faded,
    { at: step.returnAt, phase: "staggerReturn", reshuffle: step.reshuffle },
    { at: step.resetAt, phase: "idle", reshuffle: false },
  ];
}

export interface CooldownState {
  /** Current animation phase */
  phase: CooldownPhase;
  /** Number of wrong answers so far */
  wrongCount: number;
  /** Whether interaction is locked during cooldown */
  lockedOut: boolean;
  /** Whether celebrating a correct answer */
  celebrating: boolean;
  /** Whether cards are ready for staggered reappearance (hard stagger only) */
  staggerReady: boolean;
  /** Result of the last confirmed answer */
  result: "correct" | "wrong" | null;
  /** Whether the user has confirmed their selection */
  confirmed: boolean;
}

/**
 * Wrong-answer cooldown state machine. With the default
 * `QUIZ_DRAWER_COOLDOWN` it matches the prototype's timing and state
 * progression:
 *
 * Soft wrong (1st time):
 *   idle → shaking (500ms) → faded/0.3 (1400ms) → staggerReturn (1700ms) → idle
 *
 * Hard wrong (2nd+ time):
 *   idle → shaking (500ms) → faded/0 (2000ms) → staggerReturn+reshuffle (2800ms) → idle
 *
 * Correct:
 *   celebrating immediately, no cooldown
 *
 * State design: Individual useState calls are used rather than a single state object
 * because each field updates independently at different points in the animation timeline.
 * A useReducer approach would be cleaner for state transitions but would batch all
 * updates into single dispatches, which doesn't match the staggered timing requirements.
 */
export function useQuizCooldown({
  timing = QUIZ_DRAWER_COOLDOWN,
  onReshuffle,
}: {
  timing?: CooldownTiming;
  /** Deal a fresh set of options (called at a reshuffling transition). */
  onReshuffle?: () => void;
} = {}) {
  const [phase, setPhase] = useState<CooldownPhase>("idle");
  const [wrongCount, setWrongCount] = useState(0);
  const [lockedOut, setLockedOut] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [staggerReady, setStaggerReady] = useState(false);
  const [result, setResult] = useState<"correct" | "wrong" | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
    };
  }, []);

  // Two-phase mount for hard stagger (rAF-based).
  // Only schedule the rAF when the hard stagger condition is met.
  useEffect(() => {
    if (phase === "staggerReturn" && wrongCount >= timing.hardAfter) {
      const raf = requestAnimationFrame(() => setStaggerReady(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [phase, wrongCount, timing.hardAfter]);

  // Schedule a callback, tracked so unmount/reset/correct can cancel it
  const schedule = useCallback((fn: () => void, delay: number) => {
    const t = setTimeout(fn, delay);
    timersRef.current.push(t);
    return t;
  }, []);

  const triggerWrong = useCallback(() => {
    // Cancel any chain still in flight from a previous wrong answer:
    // otherwise its fullReset fires mid-cooldown and unlocks early.
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current = [];

    const newWrongCount = wrongCount + 1;
    setWrongCount(newWrongCount);
    setLockedOut(true);
    setResult("wrong");
    setConfirmed(true);
    setPhase("shaking");
    setStaggerReady(false);

    for (const step of cooldownSchedule(timing, newWrongCount)) {
      schedule(() => {
        if (step.reshuffle) onReshuffle?.();
        if (step.phase === "idle") {
          setResult(null);
          setConfirmed(false);
          setLockedOut(false);
        }
        setPhase(step.phase);
      }, step.at);
    }
  }, [wrongCount, timing, onReshuffle, schedule]);

  const triggerCorrect = useCallback(() => {
    setResult("correct");
    setConfirmed(true);
    setCelebrating(true);
    setLockedOut(true);
    // Clear any pending timers
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    setPhase("idle");
    setWrongCount(0);
    setLockedOut(false);
    setCelebrating(false);
    setStaggerReady(false);
    setResult(null);
    setConfirmed(false);
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current = [];
  }, []);

  return {
    phase,
    wrongCount,
    lockedOut,
    celebrating,
    staggerReady,
    result,
    confirmed,
    triggerWrong,
    triggerCorrect,
    reset,
  };
}
